import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { OpsOrganization } from '@gatecheckpro/contracts';
import {
  Button,
  Card,
  Field,
  LoadingRegion,
  Notice,
  Status,
  presentation,
  VERIFICATION_STATES,
} from '@gatecheckpro/design-system';
import { api } from '../lib/api.ts';
import { useResource } from '../lib/useResource.ts';
import { FailureNotice } from '../lib/refusal.tsx';

interface HistoryEntry {
  readonly occurredAt: string;
  readonly fromState: string;
  readonly toState: string;
  readonly actorDisplayName: string | null;
  readonly reasonCode: string | null;
  readonly note: string | null;
}

/**
 * The onboarding decision.
 *
 * A reason code is mandatory, and the form says why: the decision has to be
 * explainable to the organizer afterwards, and "rejected, no reason recorded" is
 * a support problem that arrives weeks later with an event already on sale
 * (SDAD Section 4.11).
 */
const REASON_CODES: readonly { value: string; label: string; decision: string }[] = [
  { value: 'REGISTRATION_VERIFIED', label: 'Registration verified', decision: 'VERIFIED' },
  { value: 'REPRESENTATIVE_CONFIRMED', label: 'Representative confirmed', decision: 'VERIFIED' },
  { value: 'AWAITING_DOCUMENTS', label: 'Awaiting documents', decision: 'IN_REVIEW' },
  { value: 'MANUAL_CHECK_STARTED', label: 'Manual check started', decision: 'IN_REVIEW' },
  { value: 'REGISTRATION_NOT_FOUND', label: 'Registration not found', decision: 'REJECTED' },
  { value: 'DETAILS_MISMATCH', label: 'Details do not match', decision: 'REJECTED' },
];

export function OrganizationReviewPage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const { resource, reload } = useResource<{
    organization: OpsOrganization;
    history: HistoryEntry[];
  }>(organizationId === undefined ? null : `/api/v1/ops/organizations/${organizationId}`);

  const [decision, setDecision] = useState<'IN_REVIEW' | 'VERIFIED' | 'REJECTED'>('IN_REVIEW');
  const [reasonCode, setReasonCode] = useState('MANUAL_CHECK_STARTED');
  const [note, setNote] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.request(`/api/v1/ops/organizations/${organizationId ?? ''}/verification`, {
        method: 'POST',
        body: { decision, reasonCode, ...(note.length > 0 ? { note } : {}) },
      });
      reload();
      if (decision !== 'IN_REVIEW') void navigate('/onboarding');
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  if (resource.status === 'loading') return <LoadingRegion label="Loading the organization" />;
  if (resource.status === 'failed') return <FailureNotice error={resource.error} />;

  const { organization, history } = resource.data;
  const state = presentation(VERIFICATION_STATES, organization.verification.state);
  const applicable = REASON_CODES.filter((reason) => reason.decision === decision);

  return (
    <div className="gc-stack gc-stack--loose">
      <div className="gc-page-header">
        <span className="gc-eyebrow">
          <Link to="/onboarding">Onboarding</Link>
        </span>
        <div className="gc-row gc-row--baseline">
          <h1>{organization.name}</h1>
          <Status tone={state.tone} glyph={state.glyph} label={state.label} title={state.meaning} />
        </div>
        <p className="gc-muted">{organization.slug}</p>
      </div>

      <Notice tone="info" title="This view is itself recorded">
        Opening an organizer&rsquo;s onboarding file is a privileged act and appears in the audit
        trail under your name.
      </Notice>

      <div className="gc-grid">
        {/*
          Submitted business-identity evidence. Class D data (SDAD Section 40):
          it reaches this page only because the caller holds
          OPS_ORGANIZER_ONBOARDING, and it is not part of the organizer-facing
          organization shape.
        */}
        <Card title="Submitted evidence" eyebrow="Business identity">
          <dl className="gc-stack gc-stack--tight" style={{ margin: 0 }}>
            <div>
              <dt className="gc-eyebrow">Registered legal name</dt>
              <dd style={{ margin: 0 }}>{organization.evidence.legalName ?? '—'}</dd>
            </div>
            <div>
              <dt className="gc-eyebrow">Registration number</dt>
              <dd style={{ margin: 0 }} className="gc-mono">
                {organization.evidence.registrationNumber ?? '—'}
              </dd>
            </div>
            <div>
              <dt className="gc-eyebrow">Authorised representative</dt>
              <dd style={{ margin: 0 }}>
                {organization.evidence.representativeName ?? '—'}
                {organization.evidence.representativeEmail !== null && (
                  <div className="gc-muted">{organization.evidence.representativeEmail}</div>
                )}
                {organization.evidence.representativePhone !== null && (
                  <div className="gc-muted gc-mono">
                    {organization.evidence.representativePhone}
                  </div>
                )}
              </dd>
            </div>
            <div>
              <dt className="gc-eyebrow">Account contact</dt>
              <dd style={{ margin: 0 }}>{organization.contactEmail}</dd>
            </div>
            <div>
              <dt className="gc-eyebrow">Country</dt>
              <dd style={{ margin: 0 }}>{organization.countryCode}</dd>
            </div>
            <div>
              <dt className="gc-eyebrow">Created</dt>
              <dd style={{ margin: 0 }} className="gc-mono">
                {new Date(organization.createdAt).toLocaleString()}
              </dd>
            </div>
          </dl>
        </Card>

        <Card title="Decision" eyebrow="Requires a reason">
          <form className="gc-stack" onSubmit={(event) => void submit(event)}>
            <FailureNotice error={error} />

            <div className="gc-field">
              <label className="gc-field__label" htmlFor="decision">
                Outcome
              </label>
              <select
                id="decision"
                className="gc-select"
                value={decision}
                onChange={(event) => {
                  const next = event.target.value as typeof decision;
                  setDecision(next);
                  const first = REASON_CODES.find((reason) => reason.decision === next);
                  if (first !== undefined) setReasonCode(first.value);
                }}
              >
                <option value="IN_REVIEW">Take into review</option>
                <option value="VERIFIED">Verify</option>
                <option value="REJECTED">Reject</option>
              </select>
            </div>

            <div className="gc-field">
              <label className="gc-field__label" htmlFor="reasonCode">
                Reason
              </label>
              <select
                id="reasonCode"
                className="gc-select"
                value={reasonCode}
                onChange={(event) => setReasonCode(event.target.value)}
              >
                {applicable.map((reason) => (
                  <option key={reason.value} value={reason.value}>
                    {reason.label}
                  </option>
                ))}
              </select>
              <span className="gc-field__hint">
                The organizer sees this. A decision they cannot understand becomes a support ticket.
              </span>
            </div>

            <Field
              label="Note (optional)"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              hint="Visible to the organizer alongside the reason."
            />

            <Button
              type="submit"
              variant={decision === 'REJECTED' ? 'destructive' : 'primary'}
              busy={busy}
            >
              {decision === 'REJECTED' ? 'Reject this organizer' : 'Record decision'}
            </Button>
          </form>
        </Card>
      </div>

      <Card title="History" eyebrow="Append-only">
        {history.length === 0 ? (
          <p className="gc-muted">Nothing recorded yet.</p>
        ) : (
          <div className="gc-table-scroll" style={{ border: 0 }}>
            <table className="gc-table">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Change</th>
                  <th scope="col">By</th>
                  <th scope="col">Reason</th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry) => (
                  <tr key={`${entry.occurredAt}-${entry.toState}`}>
                    <td className="gc-mono">{new Date(entry.occurredAt).toLocaleString()}</td>
                    <td>
                      {presentation(VERIFICATION_STATES, entry.fromState).label} →{' '}
                      <strong>{presentation(VERIFICATION_STATES, entry.toState).label}</strong>
                    </td>
                    <td>{entry.actorDisplayName ?? '—'}</td>
                    <td>
                      <div className="gc-stack gc-stack--tight">
                        <span>{entry.reasonCode ?? '—'}</span>
                        {entry.note !== null && <span className="gc-muted">{entry.note}</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
